import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import WelcomeMenuPanel from "./WelcomeMenuPanel";
import {
  useQuickReplies,
  addQuickReply,
  updateQuickReply,
  removeQuickReply,
  resetQuickReplies,
  QR_CATEGORIES,
  type QuickReply,
  type QuickReplyCategory,
} from "../services/quickRepliesService";

import { DIALOG_COLORS } from "../../../components/ui";
// ===========================================================================
// Configuración → Chat: CRUD for fast autoresponses (quick replies). Each entry
// is a reusable WhatsApp message agents can insert with one tap from the chat
// composer (/crm/tareas/chat). Backed by quickRepliesService (shared store,
// localStorage), so changes here show up in the chat immediately.
// ===========================================================================

const CATEGORY_TONE: Record<QuickReplyCategory, string> = {
  Saludo: "bg-sky-500/15 text-sky-500",
  Seguimiento: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  Cierre: "bg-emerald-500/15 text-emerald-500",
  Soporte: "bg-violet-500/15 text-violet-500",
  Agenda: "bg-indigo-500/15 text-indigo-500",
  General: "bg-gray-400/15 text-gray-500",
};

export default function ChatRepliesSettings() {
  const tc = buildThemeClasses();
  const replies = useQuickReplies();

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<QuickReplyCategory | "all">("all");
  const [modal, setModal] = useState<null | { mode: "add" | "edit"; reply?: QuickReply }>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return replies.filter((r) => {
      if (categoryFilter !== "all" && r.category !== categoryFilter) return false;
      if (!q) return true;
      return [r.title, r.text, r.shortcut].some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [replies, query, categoryFilter]);

  const confirmDelete = async (reply: QuickReply) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar respuesta",
      text: `¿Eliminar "${reply.title}"?`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (res.isConfirmed) removeQuickReply(reply.id);
  };

  const handleReset = async () => {
    const res = await Swal.fire({
      icon: "question",
      title: "Restablecer respuestas",
      text: "Se restaurarán las respuestas rápidas de ejemplo y se perderán tus cambios.",
      showCancelButton: true,
      confirmButtonText: "Restablecer",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
    });
    if (res.isConfirmed) resetQuickReplies();
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-500">
              <i className="ri-chat-quote-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Respuestas rápidas de chat</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Autorespuestas reutilizables para el chat de WhatsApp en Tareas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar respuesta..."
                className={`w-36 bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={handleReset}
              title="Restablecer ejemplos"
              className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
            >
              <i className="ri-refresh-line" />
            </button>
            <button
              onClick={() => setModal({ mode: "add" })}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className="ri-add-line" />
              <span className="whitespace-nowrap">Nueva respuesta</span>
            </button>
          </div>
        </div>

        {/* Category filter */}
        <div className="mb-4 flex flex-wrap gap-1.5">
          <CatChip label="Todas" active={categoryFilter === "all"} onClick={() => setCategoryFilter("all")} />
          {QR_CATEGORIES.map((c) => (
            <CatChip key={c} label={c} active={categoryFilter === c} onClick={() => setCategoryFilter(c)} />
          ))}
        </div>

        {/* Cards grid */}
        {filtered.length === 0 ? (
          <div className={`rounded-2xl border border-dashed p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-chat-quote-line mb-2 block text-3xl text-emerald-500" />
            <p className={`text-sm ${tc.textSecondary}`}>No hay respuestas. Crea la primera para usarla en el chat.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {filtered.map((r) => (
              <div key={r.id} className={`flex flex-col rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`truncate text-sm font-semibold ${tc.text}`}>{r.title}</p>
                    {r.shortcut && (
                      <code className={`text-[11px] ${tc.textSecondary}`}>{r.shortcut}</code>
                    )}
                  </div>
                  <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${CATEGORY_TONE[r.category]}`}>
                    {r.category}
                  </span>
                </div>
                <p className={`flex-1 whitespace-pre-wrap text-sm ${tc.textSecondary}`}>{r.text}</p>
                <div className={`mt-3 flex justify-end gap-1 border-t pt-2 ${tc.border}`}>
                  <button
                    title="Editar"
                    onClick={() => setModal({ mode: "edit", reply: r })}
                    className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                  >
                    <i className="ri-edit-line" />
                  </button>
                  <button
                    title="Eliminar"
                    onClick={() => confirmDelete(r)}
                    className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className={`mt-4 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Estas respuestas aparecen como atajos en el chat (Tareas → Chat). Escribe su comando “/atajo” en el
          composer para insertarlas al instante.
        </p>

        {/* Menú de bienvenida al primer contacto (CC-CONSOLE #1529) */}
        <WelcomeMenuPanel tc={tc} />
      </div>

      {modal && (
        <Modal
          title={modal.mode === "edit" ? "Editar respuesta rápida" : "Nueva respuesta rápida"}
          onClose={() => setModal(null)}
          themeClasses={tc}
          content={
            <ReplyForm
              tc={tc}
              initial={modal.reply}
              onCancel={() => setModal(null)}
              onSubmit={(values) => {
                if (modal.mode === "edit" && modal.reply) updateQuickReply(modal.reply.id, values);
                else addQuickReply(values);
                setModal(null);
              }}
            />
          }
        />
      )}
    </div>
  );
}

// --- Pieces -----------------------------------------------------------------

type TC = ReturnType<typeof buildThemeClasses>;

function CatChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "bg-primary-500 text-white"
          : "bg-subtle text-content-muted hover:text-content"
      }`}
    >
      {label}
    </button>
  );
}

interface FormValues {
  title: string;
  text: string;
  category: QuickReplyCategory;
  shortcut?: string;
}

function ReplyForm({
  tc, initial, onSubmit, onCancel,
}: {
  tc: TC;
  initial?: QuickReply;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<FormValues>({
    title: initial?.title ?? "",
    text: initial?.text ?? "",
    category: initial?.category ?? "General",
    shortcut: initial?.shortcut ?? "",
  });

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;
  const labelClass = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  const set = <K extends keyof FormValues>(k: K, v: FormValues[K]) => setValues((p) => ({ ...p, [k]: v }));

  const submit = () => {
    if (!values.title.trim() || !values.text.trim()) {
      Swal.fire({ icon: "warning", title: "Faltan campos", text: "El título y el mensaje son obligatorios.", confirmButtonColor: brand.primary });
      return;
    }
    let shortcut = values.shortcut?.trim();
    if (shortcut && !shortcut.startsWith("/")) shortcut = `/${shortcut}`;
    onSubmit({ ...values, title: values.title.trim(), text: values.text.trim(), shortcut: shortcut || undefined });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Título *</label>
          <input className={inputClass} value={values.title} onChange={(e) => set("title", e.target.value)} placeholder="Saludo inicial" />
        </div>
        <div>
          <label className={labelClass}>Categoría</label>
          <select className={inputClass} value={values.category} onChange={(e) => set("category", e.target.value as QuickReplyCategory)}>
            {QR_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass}>Atajo <span className="font-normal">(opcional, ej. /hola)</span></label>
        <input className={inputClass} value={values.shortcut} onChange={(e) => set("shortcut", e.target.value)} placeholder="/hola" />
      </div>
      <div>
        <label className={labelClass}>Mensaje *</label>
        <textarea rows={4} className={inputClass} value={values.text} onChange={(e) => set("text", e.target.value)} placeholder="¡Hola! Gracias por contactarnos 🙌 ¿En qué puedo ayudarte?" />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}>
          Cancelar
        </button>
        <button onClick={submit} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400">
          Guardar
        </button>
      </div>
    </div>
  );
}
